import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { images } from 'app/design/images';
import { Column } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { recordError } from 'app/lib/monitoring';
import type { TFunction } from 'i18next';
import type { ComponentType, ErrorInfo, JSX } from 'react';
import { Component } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyGraphic } from './empty-graphic';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from 'app/atoms';
import { ScrollView } from 'react-native-gesture-handler';

interface ErrorBoundaryProps {
  t: TFunction;
  reset: ReturnType<typeof useQueryErrorResetBoundary>['reset'];
  userEmail?: string;
  connectHow: 'loginWithApple' | 'email' | 'empty';
}

interface ErrorBoundaryState {
  error: Error | null;
}

function withErrorBoundaryProps<T extends ErrorBoundaryProps>(C: ComponentType<T>) {
  function ComponentWithErrorBoundaryProps(props: Omit<T, keyof ErrorBoundaryProps>): JSX.Element {
    const { t } = useTranslation();
    const { reset } = useQueryErrorResetBoundary();

    const userInfo = useAtomValue(userInfoAtom);
    const connectHow = userInfo?.email
      ? userInfo.email.includes('@privaterelay.appleid.com')
        ? 'loginWithApple'
        : 'email'
      : 'empty';

    return <C {...props} {...({ reset, t, userEmail: userInfo?.email, connectHow } as T)} />;
  }

  ComponentWithErrorBoundaryProps.displayName = `${C.displayName ?? 'Component'}WithErrorBoundaryProps`;

  return ComponentWithErrorBoundaryProps;
}

export const ErrorBoundary = withErrorBoundaryProps(
  class ErrorBoundaryComponent extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
      super(props);
      this.state = { error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
      return { error };
    }

    override componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
      recordError(error, errorInfo);
    }

    override render() {
      if (!this.state.error) return (this.props as any).children;

      return (
        <Column flex={1} gap="spacing.general.x2" justifyContent="center" padding="spacing.general.x4">
          <ScrollView showsVerticalScrollIndicator={false}>
            <EmptyGraphic
              invertTexts
              primary={this.props.t('applicationError.secondLine', {
                ns: 'common',
                connectHow: this.props.t(`applicationError.connectHow.${this.props.connectHow}`, {
                  ns: 'common',
                  email: this.props.userEmail,
                }),
              })}
              secondary={this.props.t('applicationError.firstLine', { ns: 'common' })}
              secondaryTextAlign="left"
              source={images[404]}
            />

            <Button
              marginTop="spacing.general.x4"
              onPress={() => {
                this.props.reset();
                this.setState({ error: null });
              }}
              variant="primary"
            >
              {this.props.t('applicationError.primaryButtonText', {
                ns: 'common',
              })}
            </Button>
          </ScrollView>
        </Column>
      );
    }
  },
);

export function withErrorBoundary<T extends object>(Child: ComponentType<T>) {
  function ComponentWithErrorBoundary(props: T): JSX.Element {
    return (
      <ErrorBoundary>
        <Child {...props} />
      </ErrorBoundary>
    );
  }

  ComponentWithErrorBoundary.displayName = `${Child.displayName ?? 'Component'}WithErrorBoundary`;

  return ComponentWithErrorBoundary;
}
